<template>
  <div class="d-flex justify-content-end mb-2">
    <template v-if="unreadCount > 0"> Непрочитанных сообщений{{showAll ? '' : ' из последних 200'}}: {{ unreadCount }} </template>
    <template v-else> Нет непрочитанных сообщений{{showAll ? '' : ' из последних 200'}} </template>
  </div>
  <div v-if="!showAll && hasMore" class="d-flex justify-content-end mb-2">
    <button class="btn btn-sm btn-outline-warning" @click="loadAll">
      Все сообщения
    </button>
  </div>
  <div v-if="model.data?.length" class="messages-block">
    <div
      v-for="item in model.data"
      :key="item.id"
      :ref="el => setRowRef(el, item)"
      class="one-mess"
    >
      <div :class="item.cl" class="card mb-2">
        <div class="text-mess">
          <span v-if="!item.was_read" class="new-mess">новое</span>
          <div class="only-text-mess p-2">
            <p>{{ item.message }}</p>
            <div class="footer-mess d-flex justify-content-between align-items-center">
              <span v-if="item.created_at" class="created-mess fs-12">{{ item.created_at }}</span>
              <a
                v-if="item.ref_link"
                href="#"
                class="link-mess"
                @click.prevent="goto(item.ref_link)"
                >перейти</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>Нет сообщений</div>
</template>

<script setup>
import { onMounted, ref, computed, onUnmounted } from 'vue'
import { commons } from '@provise/lib/transformer'
import { useApi } from '@provise/composables/api'
import { useRouter } from 'vue-router'

const ids = new Set()
const observer = ref(null)
const router = useRouter()
const clLevels = { 1: 'danger', 2: 'warning', 3: 'primary' }
const showAll = ref(false)

const { model, refresh } = useApi({
  url: 'notifications',
  data: {},
  pick: d => {
    d.data = d.data.map(d => {
      if (d.created_at) {
        d.created_at = commons.dateLocal(d.created_at, 'Pp')
      }
      d.cl = clLevels[d.level]
      return d
    })
    return d
  },
})

const hasMore = computed(() => model.value?.total_count > 200)

const { refresh: doRead } = useApi({
  url: 'notifications/read',
  method: 'put',
  data: {},
  transform: () => ({ notifications: Array.from(ids.values()).map(id => ({ id })) }),
})

const unreadCount = computed(() => {
  return model.value?.data?.filter(message => !message.was_read).length || 0
})

const emit = defineEmits(['modalstack:close'])
const props = defineProps({
  modalstack: {
    type: Boolean,
    default: false,
  },
  class: {
    type: String,
    default: '',
  },
  headerText: {
    type: String,
    default: '',
  },
  localHasUnread: {
    type: [Boolean, Object],
    default: false,
  }
})

const setRowRef = (el, row) => {
  if (el && !row.was_read) {
    el.__dataRow = row.id
    observer.value.observe(el)
  }
}

const onIntersect = entries => {
  for (const entry of entries) {
    if (!entry.isIntersecting) {
      continue
    }
    ids.add(entry.target.__dataRow)
  }
}

function markModelRead() {
  if (!model.value?.data || ids.size === 0) return
  model.value.data = model.value.data.map(m => (ids.has(m.id) ? { ...m, was_read: 1 } : m))
  ids.clear()
  if (props.localHasUnread && typeof props.localHasUnread === 'object') {
    props.localHasUnread.value = !!model.value.data.some(m => !m.was_read)
  }
}

async function goto(link) {
  observer.value.disconnect()
  if (ids.size > 0) {
    await doRead()
    markModelRead()
  }
  const current = new URL(window.location.href)
  const target = new URL(link, window.location.origin)

  const currentBase = current.origin + current.pathname + current.search
  const targetBase = target.origin + target.pathname + target.search

  if (currentBase === targetBase) {
    target.searchParams.set('_reload', Date.now())
    window.location.href = target.toString()
  } else {
    window.location.href = link
  }
}

async function loadAll() {
  try {
    await refresh({ query: { mode: 'all' } })
    showAll.value = true
  } catch (e) {
    console.error('loadAll error', e)
  }
}

onMounted(() => {
  observer.value = new IntersectionObserver(onIntersect, {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  })

  refresh()
})
onUnmounted(() => {
  observer.value.disconnect()
  if (ids.size > 0) {
    doRead()
    markModelRead()
    const currentUnread = unreadCount.value - ids.size
    props.localHasUnread.value = Math.max(currentUnread, 0) > 0
  }
})
</script>
